"use client";

import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { SectionReveal } from "./SectionReveal";
import { ChevronDown, Car } from "lucide-react";

const FAQ_ITEMS = [
  {
    id: 1,
    question: "How can I book a vehicle with I K TOURS AND TRAVELS?",
    answer:
      "Select Cars or Bikes, choose your service — Self Drive, Self Ride, Chauffeur or Rider — select your journey details and available vehicle, review the pricing, complete the required verification and make the payment.",
  },
  {
    id: 2,
    question: "What services can I book online?",
    answer:
      "You can book Self Drive cars, Self Ride bikes, Cars with Chauffeur and Bikes with Rider/Captain. Chauffeur/Rider bookings support One Way and Round Trip options.",
  },
  {
    id: 3,
    question: "What documents are required for Self Drive/Self Ride?",
    answer:
      "Customer verification includes required personal details and documents such as Aadhaar, Driving Licence and a recent selfie/photo. Driving Licence verification is part of the KYC process.",
  },
  {
    id: 4,
    question: "Is KYC required for Self Drive bookings?",
    answer:
      "Yes. After successful payment, the system checks whether KYC has been completed. If KYC is incomplete, the customer is prompted to complete it before proceeding with the trip.",
  },
  {
    id: 5,
    question: "What is the difference between One Way and Round Trip?",
    answer:
      "For Chauffeur/Rider bookings, One Way uses Pickup → Drop, while Round Trip supports Pickup → Sightseeing → Drop.",
  },
  {
    id: 6,
    question: "Can I add sightseeing locations to my trip?",
    answer:
      "Yes. Sightseeing locations are supported for Round Trip Chauffeur/Rider bookings.",
  },
  {
    id: 7,
    question: "How is the booking amount calculated?",
    answer:
      "The payment summary can include the applicable base fare, convenience fee, pickup/drop charges where applicable, GST, security deposit for Self Drive/Self Ride and Premium Support. Additional charges such as extra KM, extra time or damage charges may appear in the final invoice where applicable.",
  },
  {
    id: 8,
    question: "Is a security deposit applicable?",
    answer:
      "A security deposit applies to Self Drive/Self Ride bookings as configured by the business. It is not part of the Chauffeur/Rider booking charge structure.",
  },
  {
    id: 9,
    question: "How do I receive my booking confirmation?",
    answer:
      "After successful payment, the system sends booking confirmation/proforma invoice communication to the customer.",
  },
  {
    id: 10,
    question: "How does the security deposit refund work?",
    answer:
      "After the vehicle is returned, applicable calculations such as damage, extra driven KM and fuel are considered. The final invoice is generated and the security deposit is processed accordingly.",
  },
];

function FAQImage() {
  const [imgError, setImgError] = useState(false);
  return (
    <motion.div
      initial={{ opacity: 0, y: 50 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{ duration: 1.4, ease: [0.22, 0.61, 0.36, 1], delay: 0.1 }}
      className="relative mb-8 lg:mb-0"
    >
      <div className="relative rounded-2xl overflow-hidden bg-gradient-to-br from-neutral-200 to-neutral-300 dark:from-neutral-700 dark:to-neutral-800 aspect-[4/3] sm:aspect-[16/9] lg:aspect-[4/3] max-w-xl mx-auto lg:max-w-none shadow-lg">
        {!imgError ? (
          <img
            src="/images/resources/faq-one-img-1.jpg"
            alt="IK Travel car rental"
            className="h-full w-full object-cover"
            onError={() => setImgError(true)}
          />
        ) : (
          <div className="h-full w-full flex items-center justify-center">
            <Car className="w-20 h-20 text-neutral-400 dark:text-neutral-500" aria-hidden />
          </div>
        )}
      </div>
    </motion.div>
  );
}

function FAQItem({
  item,
  isOpen,
  onToggle,
}: {
  item: (typeof FAQ_ITEMS)[0];
  isOpen: boolean;
  onToggle: () => void;
}) {
  return (
    <div className="border-b border-neutral-200 dark:border-neutral-700 last:border-b-0">
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full items-center justify-between py-5 text-left"
        aria-expanded={isOpen}
        aria-controls={`faq-answer-${item.id}`}
        id={`faq-question-${item.id}`}
      >
        <h4 className="text-base md:text-lg font-semibold text-[#131222] dark:text-white pr-4">
          {item.question}
        </h4>
        <ChevronDown
          className={`w-5 h-5 shrink-0 text-[#FFB51D] transition-transform duration-200 ${
            isOpen ? "rotate-180" : ""
          }`}
          aria-hidden
        />
      </button>
      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            id={`faq-answer-${item.id}`}
            role="region"
            aria-labelledby={`faq-question-${item.id}`}
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <p className="pb-5 text-neutral-600 dark:text-neutral-400 leading-relaxed">
              {item.answer}
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export function IKFAQ() {
  const [openId, setOpenId] = useState<number>(1); // First item open by default

  return (
    <SectionReveal
      className="relative py-8 sm:py-10 md:py-12 lg:py-16"
      aria-labelledby="faq-heading"
    >
      <div className="container mx-auto px-4 md:px-6">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
          {/* Left - Section title & image */}
          <div className="lg:col-span-5 lg:sticky lg:top-24">
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 1.2, ease: [0.22, 0.61, 0.36, 1] }}
            >
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#FFB51D]/10 dark:bg-[#FFB51D]/20 border border-[#FFB51D]/30 mb-4">
                <span className="text-sm font-medium text-[#131222] dark:text-[#FFB51D]">
                  Our FAQs
                </span>
              </div>
              <h2
                id="faq-heading"
                className="text-3xl md:text-4xl font-bold text-[#131222] dark:text-white mb-6 lg:mb-8"
              >
                Frequently Asked Questions
              </h2>
            </motion.div>

            <FAQImage />
          </div>

          {/* Right - Accordion */}
          <div className="lg:col-span-7">
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 1.2, ease: [0.22, 0.61, 0.36, 1] }}
              className="rounded-2xl bg-white dark:bg-neutral-900/50 border border-neutral-200/60 dark:border-neutral-800 shadow-lg p-6 md:p-8"
            >
              <div className="space-y-0">
                {FAQ_ITEMS.map((item) => (
                  <FAQItem
                    key={item.id}
                    item={item}
                    isOpen={openId === item.id}
                    onToggle={() =>
                      setOpenId((prev) => (prev === item.id ? 0 : item.id))
                    }
                  />
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </SectionReveal>
  );
}
